import { Banner } from "@dropbox/dig-components/banner";
import { Button } from "@dropbox/dig-components/buttons";
import { LabelGroup } from "@dropbox/dig-components/label_group";
import { List } from "@dropbox/dig-components/list";
import { Text } from "@dropbox/dig-components/typography";
import { useTheme } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CheckmarkCircleFill,
  FailFill,
  SyncingLine,
} from "@dropbox/dig-icons/assets";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import {
  getConnectionMetadata,
  isBrowserExtension,
  itemIsUIConnection,
} from "@mirage/shared/connectors/util";
import i18n, {
  i18nUnlocalizableStringClass,
  meticulousRedactedStringClass,
} from "@mirage/translations";
import { useState } from "react";
import styles from "./ConnectedConnector.module.css";
import { DisconnectModal } from "./DisconnectModal";

import type {
  UIConnection,
  UIConnector,
  UIWebExtension,
} from "@mirage/shared/types";

interface Props {
  item: UIConnection | UIWebExtension;
  connector?: UIConnector | UIWebExtension;
  onDisconnect: (connection: UIConnection) => Promise<void>;
  onReauthenticate: (
    connection: UIConnection,
    connector: UIConnector | UIWebExtension,
  ) => Promise<void>;
  onConnectPrivateAccountForFederatedConnector?: (
    connector: UIConnector,
  ) => Promise<void>;
  hideDisconnect?: boolean;
  onboarding?: string;
}

export const ConnectedConnector: React.FC<Props> = ({
  item,
  connector,
  onboarding = "",
  onDisconnect,
  onReauthenticate,
  onConnectPrivateAccountForFederatedConnector,
  hideDisconnect,
}) => {
  const {
    id,
    platform,
    type,
    label,
    authenticated,
    isReauth,
    isExpired,
    teamConnector,
    connectionOwner,
    federatedConnectorOwners,
    isFederatedConnector,
    childConnection,
  } = getConnectionMetadata(item);
  const isWebExtension = isBrowserExtension(item);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const { mode } = useTheme();
  const isDarkMode = mode === "dark";
  const showSyncStatus =
    !isWebExtension &&
    item.sync_status?.sync_progress?.last_successful_sync_time === "";
  const federatedConnectorEnabled = useFeatureFlagValueBool(
    "dash_2025_03_19_slack_federated_connector",
  );
  const showFederatedConnector =
    isFederatedConnector && federatedConnectorEnabled;

  const connectedSubtext = (
    <div className={styles.connectedSubtitle}>
      <UIIcon
        size="small"
        src={CheckmarkCircleFill}
        className={styles.successColor}
      />
      <div className={styles.connectedText}>
        <Text size="small" color="faint">
          {i18n.t("connector_status_connected")}
        </Text>
      </div>
    </div>
  );

  const loadingSubtext = (
    <div className={styles.connectedSubtitle}>
      <UIIcon size="small" src={SyncingLine} className={styles.successColor} />
      <div className={styles.connectedText}>
        {isReconnecting ? (
          <Text size="small" color="faint">
            {i18n.t("connector_status_reconnecting")}
          </Text>
        ) : (
          <Text size="small" color="faint">
            {i18n.t("connector_status_disconnecting")}
          </Text>
        )}
      </div>
    </div>
  );

  const syncInProgressSubtext = (
    <div className={styles.connectedSubtitle}>
      <UIIcon size="small" src={SyncingLine} />
      <div className={styles.connectedText}>
        <Text size="small" color="faint">
          {i18n.t("connector_status_sync_in_progress")}
        </Text>
      </div>
    </div>
  );

  const expiredSubtext = (
    <div className={styles.connectedSubtitle}>
      <UIIcon size="small" src={FailFill} className={styles.errorColor} />
      <div className={styles.connectedText}>
        <Text size="small" className={styles.errorColor}>
          {i18n.t("connector_status_expired")}
        </Text>
      </div>
    </div>
  );

  const syncFailedSubtext = (
    <div className={styles.connectedSubtitle}>
      <UIIcon size="small" src={FailFill} className={styles.errorColor} />
      <div className={styles.connectedText}>
        <Text size="small" className={styles.errorColor}>
          {i18n.t("connector_status_sync_failed")}
        </Text>
      </div>
    </div>
  );

  const getSubtext = () => {
    // For federated connectors with both public and private connections, we show the private connection status
    if (showFederatedConnector && childConnection) {
      if (childConnection.loading) {
        return loadingSubtext;
      } else if (
        childConnection.sync_status?.sync_progress
          ?.last_successful_sync_time === ""
      ) {
        return syncInProgressSubtext;
      } else if (childConnection.connection_status?.[".tag"] === "connected") {
        return connectedSubtext;
      } else if (
        childConnection.connection_status?.[".tag"] === "expired" ||
        childConnection.connection_status?.[".tag"] === "reauth"
      ) {
        return expiredSubtext;
      } else {
        return syncFailedSubtext;
      }
      // Team connectors should always be shown as connected within dash
    } else if (teamConnector) {
      return connectedSubtext;
    } else {
      if (item.loading) {
        return loadingSubtext;
      } else if (showSyncStatus) {
        return syncInProgressSubtext;
      } else if (authenticated) {
        return connectedSubtext;
      } else if (isExpired || isReauth) {
        return expiredSubtext;
      } else {
        return syncFailedSubtext;
      }
    }
  };

  const connectionItem = showFederatedConnector ? childConnection : item;
  const shouldShowReauthenticateButton = showFederatedConnector
    ? childConnection &&
      childConnection.connection_status?.[".tag"] !== "connected" &&
      !isReconnecting &&
      !isDisconnecting
    : !teamConnector && !authenticated && !isReconnecting && !isDisconnecting;

  // we don't want to show the disconnect modal during onboarding
  const shouldShowDisconnectModalEntry =
    !isReconnecting &&
    !isDisconnecting &&
    !hideDisconnect &&
    (!teamConnector ||
      (teamConnector && showFederatedConnector && childConnection)) &&
    id &&
    (isWebExtension || (platform && type));

  const getConnectionOwnerText = () => {
    if (showFederatedConnector) {
      return federatedConnectorOwners.map((connectionOwner) => {
        return connectionOwner ? (
          <Text color="faint" className={meticulousRedactedStringClass}>
            <span> • </span>
            {connectionOwner}
          </Text>
        ) : null;
      });
    } else {
      if (connectionOwner) {
        return (
          <Text color="faint" className={meticulousRedactedStringClass}>
            <span> • </span>
            {connectionOwner}
          </Text>
        );
      } else {
        return null;
      }
    }
  };

  return (
    <List.Item
      className={
        onboarding ? styles.onboardingVariantListItem : styles.listItem
      }
      padding="var(--spacing__unit--1_5)"
      verticalAlign="center"
    >
      <List.Content>
        <LabelGroup
          accessoryGap={"8"}
          labelGap={"2"}
          verticalAlignment={
            showFederatedConnector && !childConnection ? "top" : "center"
          }
          withStartAccessory={
            isWebExtension ? (
              <span className={styles.iconExtraMargin}>{item.icon}</span>
            ) : (
              <img
                key={`${id}-icon`}
                src={
                  isDarkMode && item.branding?.icon_dark_src
                    ? item.branding.icon_dark_src
                    : item.branding?.icon_src
                }
                width="24px"
                height="24px"
                alt={label}
                className={styles.iconExtraMargin}
              />
            )
          }
          withLabel={
            <>
              <Text isBold className={i18nUnlocalizableStringClass}>
                {label}
              </Text>
              {getConnectionOwnerText()}
            </>
          }
          withSubText={getSubtext()}
        >
          {showFederatedConnector && !childConnection ? (
            <Banner>
              <Banner.Message>
                {i18n.t("connect_private_account_banner")}
              </Banner.Message>
              <Banner.Actions>
                <Button
                  variant="transparent"
                  onClick={() => {
                    if (
                      !isWebExtension &&
                      connector &&
                      onConnectPrivateAccountForFederatedConnector
                    ) {
                      onConnectPrivateAccountForFederatedConnector(connector);
                    }
                  }}
                >
                  {i18n.t("connect_private_content")}
                </Button>
              </Banner.Actions>
            </Banner>
          ) : null}
        </LabelGroup>
      </List.Content>
      <List.Accessory className={styles.flexCenter}>
        <>
          {shouldShowReauthenticateButton && connectionItem ? (
            <Button
              variant="primary"
              onClick={() => {
                setIsReconnecting(true);
                if (itemIsUIConnection(connectionItem) && connector) {
                  onReauthenticate(connectionItem, connector)
                    .then(() => setIsReconnecting(false))
                    .catch(() => setIsReconnecting(false));
                }
              }}
            >
              {i18n.t("reauthenticate")}
            </Button>
          ) : null}
          {shouldShowDisconnectModalEntry && connectionItem ? (
            <DisconnectModal
              label={label ?? ""}
              item={connectionItem}
              onDisconnect={(connectionItem) => {
                setIsDisconnecting(true);
                onDisconnect(connectionItem)
                  .then(() => setIsReconnecting(false))
                  .catch(() => setIsReconnecting(false));
              }}
              isFederatedConnector={showFederatedConnector}
            />
          ) : null}
        </>
      </List.Accessory>
    </List.Item>
  );
};
